import { Anchor, Box, Heading, Icon, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { useContext } from 'react';
import { InboxPageMobileView } from '../../../../types/InboxPageMobileView';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { useConversationMessagesQuery } from '../../../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../../../utils/conversationMessageUtils';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import conversationUtils from '../../../../utils/conversationUtils';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '@zg-rentals/satellite-components';
import ContactActions from '../ContactActions';
import InboxSidebarHeaderSkeleton from './skeleton';

export type HeaderData = {
  isPhoneLead: boolean;
  renterName: string;
  renterPhone: string;
  streetAddress: string;
  isActive: boolean;
  isConnectedExperience: boolean;
};

export const selectHeaderData = (data: ConversationMessagesData): HeaderData => ({
  isPhoneLead: data.isPhoneLead,
  renterName: data.renterName ?? '',
  renterPhone: data.renterPhone ?? '',
  streetAddress: data.listingDetails.streetAddress ?? '',
  isActive: data.listingDetails.isActive,
  isConnectedExperience: data.listingDetails.isConnectedExperience,
});

// Connected-experience listings link to the newer "find tenants" dashboard;
// everything else uses the legacy listing page.
export const getListingUrl = (listingAlias: string | undefined, isConnectedExperience: boolean): string => {
  const pathname = isConnectedExperience ? 'find-tenants' : 'listing';
  return `/rental-manager/properties/${listingAlias}/${pathname}`;
};

export default function Header(): JSX.Element {
  const { conversationId, listingAlias, clientTimeZone } = useContext(InboxPageContext);
  const { changeMobileView } = useInboxRouterHelper();
  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: headerData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectHeaderData,
  });

  const handleArrowClick = () => {
    changeMobileView(InboxPageMobileView.MessageList);
  };

  if (!headerData) {
    return <InboxSidebarHeaderSkeleton />;
  }

  const { isPhoneLead, renterName, renterPhone, streetAddress, isActive, isConnectedExperience } = headerData;
  const listingUrl = getListingUrl(listingAlias, isConnectedExperience);
  const listingAddress = streetAddress.split(',')[0];
  const displayName = isPhoneLead
    ? conversationUtils.getPhoneLeadParticipantDisplay({
        name: renterName,
        phoneNumber: renterPhone,
        shouldFormatPhoneNumber: true,
      })
    : renterName;

  return (
    <>
      <IconButton
        shape="square"
        emphasis="bare"
        tone="brand"
        onClick={handleArrowClick}
        icon={<Icon size="sm" render={<IconChevronLeftFilled />} />}
        title="Go back to messages"
        css={{
          xxl: {
            display: 'none',
          },
        }}
      />
      <Box
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'default',
        }}
      >
        <Heading
          level={2}
          className={FULLSTORY_EXCLUDE_CLASS_NAME}
          textStyle="heading-xs"
          css={{ textTransform: 'capitalize', textAlign: 'center' }}
        >
          {displayName}
        </Heading>

        <Paragraph
          textStyle="body-sm"
          css={{ marginBottom: 'default', marginTop: 'tightest', color: 'text.subtle', textAlign: 'center' }}
        >
          For&nbsp;
          <Anchor href={listingUrl}>{listingAddress}</Anchor>
          {!isActive && (
            <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
              &nbsp;(Off Market)
            </Text>
          )}
        </Paragraph>

        <ContactActions />
      </Box>
    </>
  );
}
